import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import axios from 'axios'

// ** Async Thunks
export const fetchTransactions = createAsyncThunk(
  'transactions/fetchTransactions',
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await axios.get('/transactions', { params })
      return response.data
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch transactions')
    }
  }
)

export const fetchTransactionById = createAsyncThunk(
  'transactions/fetchTransactionById',
  async (transactionId, { rejectWithValue }) => {
    try {
      const response = await axios.get(`/transactions/${transactionId}`)
      return response.data
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch transaction')
    }
  }
)

export const createTransaction = createAsyncThunk(
  'transactions/createTransaction',
  async (transactionData, { rejectWithValue }) => {
    try {
      const response = await axios.post('/transactions', transactionData)
      return response.data
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to create transaction')
    }
  }
)

export const updateTransaction = createAsyncThunk(
  'transactions/updateTransaction',
  async ({ transactionId, transactionData }, { rejectWithValue }) => {
    try {
      const response = await axios.put(`/transactions/${transactionId}`, transactionData)
      return response.data
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to update transaction')
    }
  }
)

export const refundTransaction = createAsyncThunk(
  'transactions/refundTransaction',
  async ({ transactionId, refundData }, { rejectWithValue }) => {
    try {
      const response = await axios.post(`/transactions/${transactionId}/refund`, refundData)
      return response.data
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to refund transaction')
    }
  }
)

export const voidTransaction = createAsyncThunk(
  'transactions/voidTransaction',
  async (transactionId, { rejectWithValue }) => {
    try {
      const response = await axios.post(`/transactions/${transactionId}/void`)
      return response.data
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to void transaction')
    }
  }
)

// ** Initial State
const initialState = {
  transactions: [],
  selectedTransaction: null,
  loading: false,
  error: null,
  pagination: {
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 10
  },
  filters: {
    search: '',
    status: '',
    gateway: '',
    dateRange: null,
    amountRange: null,
    paymentMethod: ''
  },
  sort: {
    field: 'createdAt',
    direction: 'desc'
  },
  statistics: {
    totalAmount: 0,
    totalTransactions: 0,
    successRate: 0,
    averageAmount: 0
  }
}

// ** Slice
const transactionSlice = createSlice({
  name: 'transactions',
  initialState,
  reducers: {
    setSelectedTransaction: (state, action) => {
      state.selectedTransaction = action.payload
    },
    clearSelectedTransaction: (state) => {
      state.selectedTransaction = null
    },
    setFilters: (state, action) => {
      state.filters = { ...state.filters, ...action.payload }
    },
    clearFilters: (state) => {
      state.filters = initialState.filters
    },
    setSort: (state, action) => {
      state.sort = action.payload
    },
    setPagination: (state, action) => {
      state.pagination = { ...state.pagination, ...action.payload }
    },
    setStatistics: (state, action) => {
      state.statistics = { ...state.statistics, ...action.payload }
    },
    clearError: (state) => {
      state.error = null
    },
    resetTransactionState: () => initialState,
    // Real-time updates
    updateTransactionStatus: (state, action) => {
      const { transactionId, status } = action.payload
      const transaction = state.transactions.find(t => t.TransactionId === transactionId)
      if (transaction) {
        transaction.Status = status
      }
      if (state.selectedTransaction?.TransactionId === transactionId) {
        state.selectedTransaction.Status = status
      }
    },
    addNewTransaction: (state, action) => {
      state.transactions.unshift(action.payload)
      state.pagination.totalItems += 1
    }
  },
  extraReducers: (builder) => {
    builder
      // Fetch Transactions
      .addCase(fetchTransactions.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchTransactions.fulfilled, (state, action) => {
        state.loading = false
        state.transactions = action.payload.data || action.payload
        state.pagination = {
          ...state.pagination,
          ...action.payload.pagination
        }
        state.statistics = {
          ...state.statistics,
          ...action.payload.statistics
        }
      })
      .addCase(fetchTransactions.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })
      
      // Fetch Transaction by ID
      .addCase(fetchTransactionById.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchTransactionById.fulfilled, (state, action) => {
        state.loading = false
        state.selectedTransaction = action.payload
      })
      .addCase(fetchTransactionById.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })
      
      // Create Transaction
      .addCase(createTransaction.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(createTransaction.fulfilled, (state, action) => {
        state.loading = false
        state.transactions.unshift(action.payload)
        state.pagination.totalItems += 1
        state.selectedTransaction = action.payload
      })
      .addCase(createTransaction.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })
      
      // Update Transaction
      .addCase(updateTransaction.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(updateTransaction.fulfilled, (state, action) => {
        state.loading = false
        const index = state.transactions.findIndex(t => t.TransactionId === action.payload.TransactionId)
        if (index !== -1) {
          state.transactions[index] = action.payload
        }
        if (state.selectedTransaction?.TransactionId === action.payload.TransactionId) {
          state.selectedTransaction = action.payload
        }
      })
      .addCase(updateTransaction.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })
      
      // Refund Transaction
      .addCase(refundTransaction.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(refundTransaction.fulfilled, (state, action) => {
        state.loading = false
        const index = state.transactions.findIndex(t => t.TransactionId === action.payload.TransactionId)
        if (index !== -1) {
          state.transactions[index] = action.payload
        }
        if (state.selectedTransaction?.TransactionId === action.payload.TransactionId) {
          state.selectedTransaction = action.payload
        }
      })
      .addCase(refundTransaction.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })
      
      // Void Transaction
      .addCase(voidTransaction.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(voidTransaction.fulfilled, (state, action) => {
        state.loading = false
        const index = state.transactions.findIndex(t => t.TransactionId === action.payload.TransactionId)
        if (index !== -1) {
          state.transactions[index] = action.payload
        }
        if (state.selectedTransaction?.TransactionId === action.payload.TransactionId) {
          state.selectedTransaction = action.payload
        }
      })
      .addCase(voidTransaction.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })
  }
})

export const {
  setSelectedTransaction,
  clearSelectedTransaction,
  setFilters,
  clearFilters,
  setSort,
  setPagination,
  setStatistics,
  clearError,
  resetTransactionState,
  updateTransactionStatus,
  addNewTransaction
} = transactionSlice.actions

export default transactionSlice.reducer
